import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Eye, Brain, Baby, Glasses, HeartPulse, Sparkles, Ear, MapPin, Phone, Mail, Clock, CheckCircle2, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import VisualCheckup from "../components/VisualCheckup";
import HomeVideo from "../components/HomeVideo";
import VisionIntegral from "../components/VisionIntegral";
import GoogleReviews from "../components/GoogleReviews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_HERO = "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/0zz7eg9a_Logo%20extendido_.jpg";

const SERVICES = [
  {
    icon: Eye,
    title: "Evaluación de eficacia visual",
    desc: "Examen exhaustivo del sistema visual: salud ocular, refracción, movimientos oculares, habilidades acomodativas, binoculares y estereopsis. Con informe detallado.",
    slug: "eficacia-visual",
  },
  {
    icon: Brain,
    title: "Procesamiento visual y auditivo",
    desc: "Evaluación profunda cuando hay dificultades de aprendizaje o lesión cerebral. Batería de tests estandarizados de percepción visual y auditiva.",
    slug: "procesamiento-visual-auditivo",
  },
  {
    icon: Brain,
    title: "Terapia visual (optometría comportamental)",
    desc: "Programa neurofuncional individual: 45 min en consulta 1-2 veces/semana + 15 min/día en casa. Crea nuevas conexiones neuronales.",
    slug: "terapia-visual",
  },
  {
    icon: Baby,
    title: "Optometría infantil",
    desc: "Examen adaptado a los más pequeños desde los 2 años. Detección temprana de ambliopía, estrabismo y dificultades de aprendizaje.",
    slug: "optometria-infantil",
  },
  {
    icon: Glasses,
    title: "Contactología avanzada",
    desc: "RPG, escleral, corneo-esclerales, multifocales y tóricas. Adaptación en córneas regulares e irregulares (queratocono, post-cirugía).",
    slug: "contactologia",
  },
  {
    icon: HeartPulse,
    title: "Control de miopía",
    desc: "Ortoqueratología (orto-K), lentes MiSight®, MyLo® y gafas con tecnología específica. Frenamos el avance de la miopía en niños y adolescentes.",
    slug: "control-miopia",
  },
  {
    icon: Activity,
    title: "SDP · Síndrome de Deficiencia Postural",
    desc: "Cuando dolor de cabeza, mareos, vértigo o tensión postural tienen un origen visual. Evaluación coordinada con fisios y osteópatas, y prismas posturales.",
    slug: "sdp",
  },
  {
    icon: Sparkles,
    title: "WIVI · Entrenamiento visual con IA",
    desc: "Sistema clínico avanzado que evalúa y entrena más de 100 parámetros de la función visual con inteligencia artificial y tecnología 3D interactiva.",
    slug: "wivi",
  },
  {
    icon: Ear,
    title: "Audiología y audífonos",
    desc: "Audiometría completa, estudio personalizado y adaptación de audífonos de última generación. Revisiones y seguimiento continuo.",
    slug: "audiologia-audifonos",
  },
  {
    icon: Ear,
    title: "Terapia auditiva",
    desc: "Reeducación del procesamiento auditivo con los métodos Berard, Tomatis y SENA. Clave en aprendizaje, lenguaje y atención.",
    slug: "terapia-auditiva",
  },
  {
    icon: Glasses,
    title: "Gafas y monturas de diseño",
    desc: "Lentes oftálmicas de precisión y monturas seleccionadas de firmas de calidad y diseño para toda la familia.",
    slug: "opticas",
  },
];

const HomePage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", consent_data_protection: false, consent_marketing: false, website: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_data_protection) {
      toast.error("Debe aceptar la política de protección de datos para enviar el mensaje.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensaje enviado. Te contactaremos pronto.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", consent_data_protection: false, consent_marketing: false, website: "" });
    } catch {
      toast.error("No se pudo enviar el mensaje. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="home-page">
      {/* ==== HERO ==== */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, var(--bg-soft) 0%, var(--bg) 100%)" }}>
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center relative">
          <div className="md:col-span-7 fade-up">
            <span className="section-eyebrow" data-testid="hero-eyebrow">✦ Centro de Optometría en Madrid</span>
            <h1 className="section-title mt-5">
              Cuidamos tu <span className="handwritten">visión</span>
              <br />con mirada humana.
            </h1>
            <p className="mt-6 max-w-xl font-sans-body text-[color:var(--ink-soft)] text-lg">
              En Salud y Visión te acompañamos en cada etapa — desde los primeros años hasta la edad adulta — con optometría clínica, terapia visual y contactología de alta precisión.
            </p>
            <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-actions">
              <a href="#autoevaluacion" className="btn-primary" data-testid="hero-cta-autoeval">
                <span className="flex flex-col items-start leading-tight">
                  <span>Haz tu autoevaluación visual</span>
                  <span className="text-xs font-normal opacity-85">para saber si necesitas nuestra ayuda</span>
                </span>
                <ArrowRight size={18} />
              </a>
              <a href="#contacto" className="btn-ghost" data-testid="hero-cta-contact">Pedir cita <Phone size={16} /></a>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 font-sans-body text-sm text-[color:var(--ink-soft)]">
              <div className="flex items-center gap-2"><CheckCircle2 size={18} className="text-[color:var(--sage-500)]" /> +20 años de experiencia</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={18} className="text-[color:var(--sage-500)]" /> Terapia visual personalizada</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={18} className="text-[color:var(--sage-500)]" /> Todas las edades</div>
            </div>
          </div>
          <div className="md:col-span-5 relative fade-up delay-200">
            {/* Monograma S y V — sello decorativo grande */}
            <img
              src="https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/sy6zuzxz_S%20y%20V.jpg"
              alt=""
              aria-hidden="true"
              className="absolute -top-10 -right-8 h-36 md:h-48 w-36 md:w-48 object-contain hidden md:block z-20"
              style={{ mixBlendMode: "multiply", transform: "rotate(8deg)", filter: "drop-shadow(0 10px 20px rgba(54,66,32,0.15))" }}
            />
            <div className="relative rounded-[2rem] overflow-hidden" style={{ border: "1px solid var(--sage-100)", boxShadow: "0 30px 60px -30px rgba(54,66,32,0.35)", height: "520px" }}>
              <img
                src="https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/5prsbdj1_image%20%282%29.png"
                alt="Niña con gafas concentrada escribiendo — Salud y Visión"
                className="w-full h-full object-cover"
                style={{ filter: "saturate(1.05) contrast(1.03)" }}
              />
              <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(54,66,32,0) 0%, rgba(54,66,32,0.65) 100%)" }} />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-sans-body text-xs uppercase tracking-widest mb-1" style={{ color: "var(--sage-200)", textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>Desde los primeros años</div>
                <div className="font-display text-3xl md:text-4xl" style={{ color: "var(--cream)", textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>mirada viva ✦</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 soft-card hidden md:block" style={{ padding: "1rem 1.25rem" }}>
              <div className="font-display text-3xl" style={{ color: "var(--sage-500)" }}>✦ desde 2001</div>
              <div className="font-sans-body text-xs text-[color:var(--ink-soft)]">cuidando miradas en Madrid</div>
            </div>
          </div>
        </div>
      </section>

      {/* ==== SERVICIOS ==== */}
      <section id="servicios" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <span className="section-eyebrow">Nuestros servicios</span>
            <h2 className="section-title mt-4">Una visión integral, <span className="handwritten">paso a paso</span></h2>
            <p className="font-sans-body text-[color:var(--ink-soft)] mt-5">
              Cada persona ve el mundo de manera única. Por eso diseñamos un plan específico para cada paciente, combinando precisión clínica con un trato humano cercano.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-14" data-testid="services-grid">
            {SERVICES.map((s, i) => (
              <Link key={i} to={`/servicios#${s.slug}`} className="soft-card fade-up group block" style={{ animationDelay: `${0.05 * i}s` }} data-testid={`service-${i}`}>
                <div className="h-12 w-12 rounded-full flex items-center justify-center mb-5 transition-colors group-hover:bg-[color:var(--sage-200)]" style={{ background: "var(--sage-50)" }}>
                  <s.icon size={22} style={{ color: "var(--sage-500)" }} />
                </div>
                <h3 className="text-xl font-medium" style={{ color: "var(--ink)" }}>{s.title}</h3>
                <p className="mt-2 font-sans-body text-sm text-[color:var(--ink-soft)]">{s.desc}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-sans-body font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--sage-600)" }}>
                  Saber más <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/servicios" className="btn-ghost" data-testid="view-all-services">
              Ver todos los servicios en detalle <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ==== VÍDEO ==== */}
      <HomeVideo />

      {/* ==== VISIÓN INTEGRAL · Diagrama + WIVI ==== */}
      <VisionIntegral />

      {/* ==== SÍNTOMAS QUE NO SON LO QUE PARECEN — gancho a la página de Síntomas ==== */}
      <section className="py-24 px-6" style={{ background: "var(--bg)" }} data-testid="te-suena-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center fade-up">
            <span className="section-eyebrow">⚠ Síntomas que no son lo que parecen</span>
            <h2 className="section-title mt-4">
              A veces no es <span className="handwritten">falta de atención</span>
              <br />ni cansancio
            </h2>
            <p className="font-sans-body text-[color:var(--ink-soft)] mt-6 max-w-2xl mx-auto leading-relaxed text-lg">
              Muchos problemas que vivimos como <em>pereza</em>, <em>distracción</em> o <em>vista cansada</em> esconden detrás un problema visual no detectado. ¿Te has reconocido en alguna de estas situaciones?
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: "🎒", title: "Tu hijo/a en el cole", text: "Se cansa al hacer deberes, se salta líneas al leer, le cuesta atender — pero le han dicho que tiene 'falta de atención'." },
              { icon: "👁️", title: "Ojo vago o estrabismo", text: "Te dijeron 'ya es tarde' o que el parche era la única opción. Spoiler: ni una cosa ni la otra." },
              { icon: "📱", title: "Pantallas todo el día", text: "Visión borrosa, dolor de cabeza al cerrar el ordenador, sequedad ocular constante. No es solo cansancio." },
              { icon: "🤕", title: "Dolores sin causa clara", text: "Cabeza, cuello o espalda que vuelven una y otra vez aunque te trate el fisio. La visión dirige la postura." },
              { icon: "👶", title: "Tu peque pequeño", text: "Se acerca mucho al papel, guiña los ojos, gira la cabeza para mirar. Él no sabe que algo no va bien — tú sí puedes verlo." },
              { icon: "📖", title: "Lectura difícil", text: "Las letras 'se mueven' o 'se hacen borrosas' al rato de leer. Y nadie te ha encontrado nada en la graduación." },
            ].map((item, i) => (
              <div key={i} className="soft-card p-6" style={{ animationDelay: `${0.05 * i}s` }} data-testid={`te-suena-card-${i}`}>
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-display text-xl mb-2" style={{ color: "var(--ink)" }}>{item.title}</h3>
                <p className="font-sans-body text-sm text-[color:var(--ink-soft)] leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/sintomas" className="btn-primary" data-testid="te-suena-cta">
              Ver todos los síntomas <ArrowRight size={16} />
            </Link>
            <p className="font-sans-body text-xs text-[color:var(--ink-soft)] mt-4">
              Y recuerda: <strong>tiene solución, a cualquier edad.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ==== NOSOTROS ==== */}
      <section id="nosotros" className="py-24" style={{ background: "var(--sage-50)" }}>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden" style={{ border: "1px solid var(--sage-100)", boxShadow: "0 30px 60px -30px rgba(54,66,32,0.3)", height: "560px" }}>
              <img
                src="https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/odwl1ysf_entrada.jpg"
                alt="Entrada del centro — Salud y Visión"
                className="w-full h-full object-cover"
                style={{ filter: "saturate(1.05) contrast(1.03)" }}
              />
            </div>
            <div className="absolute -top-5 -left-5 font-display text-5xl hidden md:block" style={{ color: "var(--sage-400)" }}>✦</div>
            <div className="absolute -bottom-8 -right-4 soft-card hidden md:block max-w-[240px]" style={{ padding: "1.1rem 1.25rem" }}>
              <div className="font-sans-body text-xs uppercase tracking-widest" style={{ color: "var(--sage-500)" }}>Ven a vernos</div>
              <div className="font-display text-2xl mt-1" style={{ color: "var(--ink)" }}>Te esperamos<br />en Madrid ✦</div>
            </div>
          </div>
          <div>
            <span className="section-eyebrow">Sobre nosotros</span>
            <h2 className="section-title mt-4">Optometría con <span className="handwritten">corazón</span></h2>
            <p className="font-sans-body text-[color:var(--ink-soft)] mt-5 leading-relaxed">
              Somos un equipo de optometristas especializados en terapia visual, rehabilitación visual y contactología avanzada. Creemos que ver bien va mucho más allá de enfocar bien: implica comprender, aprender y disfrutar de lo que nos rodea.
            </p>
            <p className="font-sans-body text-[color:var(--ink-soft)] mt-4 leading-relaxed">
              Trabajamos de la mano con pedagogos, logopedas y otros profesionales para ofrecer un abordaje completo a cada paciente, sea un niño con dificultades de aprendizaje o un adulto que busca mejorar su rendimiento visual.
            </p>
            <div className="grid grid-cols-3 gap-4 mt-10 font-sans-body">
              {[["20+", "años"], ["3500+", "pacientes"], ["100%", "personalizado"]].map((x) => (
                <div key={x[0]} className="soft-card text-center" style={{ padding: "1.25rem 0.5rem" }}>
                  <div className="font-display text-4xl" style={{ color: "var(--sage-500)" }}>{x[0]}</div>
                  <div className="text-xs uppercase tracking-widest text-[color:var(--ink-soft)] mt-1">{x[1]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==== TESTIMONIOS GOOGLE ==== */}
      <GoogleReviews />

      {/* ==== AUTOEVALUACIÓN ==== */}
      <VisualCheckup />

      {/* ==== CONTACTO ==== */}
      <section id="contacto" className="py-24" style={{ background: "var(--bg-soft)" }}>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14">
          <div>
            <span className="section-eyebrow">Contacto</span>
            <h2 className="section-title mt-4">Ven a <span className="handwritten">conocernos</span></h2>
            <p className="font-sans-body text-[color:var(--ink-soft)] mt-5 max-w-md">
              Estamos encantados de ayudarte. Escríbenos, llámanos o acércate al centro.
            </p>

            <div className="mt-8 space-y-5 font-sans-body">
              <div className="flex gap-4">
                <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--sage-100)" }}><MapPin size={18} style={{ color: "var(--sage-600)" }} /></div>
                <div><div className="font-semibold text-[color:var(--ink)]">Dirección</div><div className="text-sm text-[color:var(--ink-soft)]">C/ Alcalde López Casero 10, Madrid</div></div>
              </div>
              <div className="flex gap-4">
                <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--sage-100)" }}><Phone size={18} style={{ color: "var(--sage-600)" }} /></div>
                <div><div className="font-semibold text-[color:var(--ink)]">Teléfonos</div><div className="text-sm text-[color:var(--ink-soft)]"><span className="text-xs uppercase tracking-widest" style={{ color: "var(--sage-600)" }}>General</span> · 91 404 48 77<br /><span className="text-xs uppercase tracking-widest" style={{ color: "var(--sage-600)" }}>Terapia visual</span> · 657 95 78 21<br /><span className="text-xs uppercase tracking-widest" style={{ color: "var(--sage-600)" }}>Óptica</span> · 640 81 23 28</div></div>
              </div>
              <div className="flex gap-4">
                <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--sage-100)" }}><Mail size={18} style={{ color: "var(--sage-600)" }} /></div>
                <div><div className="font-semibold text-[color:var(--ink)]">Email</div><div className="text-sm text-[color:var(--ink-soft)] break-all">centrodeoptometriasaludyvision@gmail.com</div></div>
              </div>
              <div className="flex gap-4">
                <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--sage-100)" }}><Clock size={18} style={{ color: "var(--sage-600)" }} /></div>
                <div><div className="font-semibold text-[color:var(--ink)]">Horario</div><div className="text-sm text-[color:var(--ink-soft)]">Lunes a Viernes · 10:00 — 14:30 y 16:30 — 20:00<br />Sábados · 10:15 — 14:00</div></div>
              </div>
            </div>

            <iframe title="Mapa" className="mt-8 w-full h-64 rounded-2xl border-0" src="https://www.google.com/maps?q=Calle+Alcalde+Lopez+Casero+10,+Madrid&output=embed"></iframe>
          </div>

          <form onSubmit={submit} className="soft-card" data-testid="contact-form">
            <h3 className="text-2xl font-medium" style={{ color: "var(--ink)" }}>Envíanos un mensaje</h3>
            <p className="font-sans-body text-sm text-[color:var(--ink-soft)] mt-2">Respondemos en 24-48 h.</p>
            {/* Honeypot anti-spam — invisible para humanos, los bots lo rellenan */}
            <input
              type="text"
              name="website"
              tabIndex="-1"
              autoComplete="off"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
              aria-hidden="true"
            />
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <div>
                <label className="field-label">Nombre</label>
                <input required className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
              </div>
              <div>
                <label className="field-label">Teléfono</label>
                <input className="field-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">Email</label>
                <input required type="email" className="field-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">Asunto</label>
                <input className="field-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">Mensaje</label>
                <textarea required rows="5" className="field-textarea" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message"></textarea>
              </div>
              <div className="md:col-span-2 mt-2 space-y-3">
                <label
                  className="flex items-start gap-3 cursor-pointer rounded-xl p-3"
                  style={{
                    background: form.consent_data_protection ? "var(--sage-50)" : "var(--cream)",
                    border: `1px solid ${form.consent_data_protection ? "var(--sage-300, #b6c594)" : "var(--sage-100)"}`,
                    transition: "all 0.2s",
                  }}
                  data-testid="contact-consent-data"
                >
                  <input
                    type="checkbox"
                    checked={form.consent_data_protection}
                    onChange={(e) => setForm({ ...form, consent_data_protection: e.target.checked })}
                    style={{ accentColor: "var(--sage-500)", marginTop: "3px", width: 18, height: 18 }}
                    data-testid="contact-consent-data-checkbox"
                    required
                  />
                  <span className="font-sans-body text-sm" style={{ color: "var(--ink)" }}>
                    He leído y acepto la <Link to="/legal" target="_blank" className="underline" style={{ color: "var(--sage-600)" }}>política de protección de datos</Link>. <span style={{ color: "var(--terracotta)" }}>*</span>
                  </span>
                </label>
                <label
                  className="flex items-start gap-3 cursor-pointer rounded-xl p-3"
                  style={{ background: "var(--cream)", border: "1px solid var(--sage-100)" }}
                  data-testid="contact-consent-marketing"
                >
                  <input
                    type="checkbox"
                    checked={form.consent_marketing}
                    onChange={(e) => setForm({ ...form, consent_marketing: e.target.checked })}
                    style={{ accentColor: "var(--sage-500)", marginTop: "3px", width: 18, height: 18 }}
                    data-testid="contact-consent-marketing-checkbox"
                  />
                  <span className="font-sans-body text-sm" style={{ color: "var(--ink-soft)" }}>
                    Acepto recibir información sobre productos, servicios y comunicaciones de cortesía (opcional).
                  </span>
                </label>
              </div>
            </div>
            <button type="submit" disabled={sending} className="btn-primary mt-6" data-testid="contact-submit">
              {sending ? "Enviando..." : "Enviar mensaje"} <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
